/**
 * Settings → Bot → Persönlichkeit.
 *
 * Avatar, Name, Soul-Text. Aus SettingsView gezogen, damit die große
 * Datei nur noch zusammensteckt und diese Karte allein testbar ist.
 */
import {
  fetchPersona,
  resetPersona,
  savePersona,
  type SoulProfileMeta,
} from "../persona-api.js";

/** Bild in den Kreis. Fehlt die Datei, Initial statt leerem Loch. */
function haengeAvatarBild(
  circle: HTMLElement,
  src: string,
  label: string,
  fallbackKey: string,
): void {
  const img = document.createElement("img");
  img.src = src;
  img.alt = label;
  img.loading = "lazy";
  img.className = "settings-avatar-img";
  img.addEventListener("error", () => {
    img.remove();
    circle.dataset.fallback = fallbackKey;
    circle.textContent = label.charAt(0).toUpperCase() || "?";
  });
  circle.appendChild(img);
}

/**
 * Pre.155: Eigenes Avatar-Bild hochladen. Öffnet File-Picker, lädt
 * das Bild über /api/persona/avatar hoch (max 2 MB, png/jpeg/webp),
 * Server speichert in /uploads/avatars/ und merkt sich die URL in
 * der user-persona-config.
 */
async function handleAvatarUpload(
  flashStatus: (msg: string) => void,
  onAvatarSaved: () => void,
): Promise<void> {
  // Sofortiges Feedback — zeigt beim Debuggen, dass der Handler feuert.
  flashStatus("Öffne Foto-Auswahl …");
  // Waisen-Sweep: liegengebliebenes Input eines frueheren Versuchs entfernen.
  for (const el of Array.from(
    document.querySelectorAll(".settings-avatar-file-input"),
  ))
    el.remove();
  const input = document.createElement("input");
  input.className = "settings-avatar-file-input";
  input.type = "file";
  input.accept = "image/png,image/jpeg,image/webp";
  // 2026-07-02 (V3): Unsichtbarkeit kommt aus der Klasse — der Input
  // MUSS im Viewport bleiben (siehe CSS-Kommentar .visually-hidden-input).
  input.classList.add("visually-hidden-input");
  document.body.appendChild(input);
  const cleanup = (): void => {
    try {
      input.remove();
    } catch {
      /* schon entfernt */
    }
  };
  input.addEventListener("change", async () => {
    const file = input.files?.[0];
    if (!file) {
      cleanup();
      return;
    }
    flashStatus("Lade hoch…");
    try {
      const { uploadCustomAvatar } = await import("../persona-api.js");
      // 2026-07-02 (Martin): iPhone-Fotos (3-10 MB) sprengten das Server-
      // Limit — fuer einen Avatar reichen 512px. Vor dem Upload verkleinern;
      // scheitert das Dekodieren, geht das Original raus (Server-Reserve).
      let toSend: File = file;
      try {
        const bmp = await createImageBitmap(file);
        const max = 512;
        const scale = Math.min(1, max / Math.max(bmp.width, bmp.height));
        const w = Math.max(1, Math.round(bmp.width * scale));
        const h = Math.max(1, Math.round(bmp.height * scale));
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        canvas.getContext("2d")?.drawImage(bmp, 0, 0, w, h);
        bmp.close();
        const blob = await new Promise<Blob | null>((res) =>
          canvas.toBlob(res, "image/jpeg", 0.85),
        );
        if (blob && blob.size > 0)
          toSend = new File([blob], "avatar.jpg", { type: "image/jpeg" });
      } catch {
        /* Original senden */
      }
      const result = await uploadCustomAvatar(toSend);
      cleanup();
      if (!result.ok) {
        flashStatus(`Upload-Fehler: ${result.error ?? "unbekannt"}`);
        return;
      }
      flashStatus("Avatar gespeichert");
      // SettingsView komplett neu rendern, damit der neue Avatar sichtbar wird.
      onAvatarSaved();
    } catch (err) {
      cleanup();
      const msg = err instanceof Error ? err.message : String(err);
      flashStatus(`Upload-Fehler: ${msg}`);
    }
  });
  // 2026-07-01 (Martin): KEIN zeitbasiertes Aufraeumen — auf iOS kehrte der
  // Fokus zurueck, WAEHREND der Foto-Picker noch offen war → das Input wurde
  // entfernt, bevor 'change' feuern konnte ("kann Foto nicht einsetzen").
  // Aufraeumen: im change-Handler, beim Abbrechen ('cancel'), Waisen-Sweep.
  input.addEventListener("cancel", cleanup);
  // showPicker() ist die moderne API (WebKit 16.4+); Fallback click().
  try {
    const anyInput = input as HTMLInputElement & {
      showPicker?: () => void;
    };
    if (typeof anyInput.showPicker === "function") anyInput.showPicker();
    else input.click();
  } catch (err) {
    // z.B. NotAllowedError ausserhalb einer User-Geste — sichtbar machen
    // statt still zu scheitern, und den klassischen Weg versuchen.
    flashStatus(
      `Foto-Auswahl: ${err instanceof Error ? err.message : String(err)}`,
    );
    try {
      input.click();
    } catch {
      /* wirklich blockiert */
    }
  }
}


export function createPersonaBlock(opts: {
  flashStatus: (msg: string) => void;
  onAvatarSaved: () => void;
}): HTMLElement {
  // Pre.423: Card-Layout matching Sprache-Tab.
  const wrap = document.createElement("section");
  wrap.className = "settings-subsection settings-persona";

  const head = document.createElement("h3");
  head.className = "settings-subsection-title";
  head.textContent = "Persönlichkeit";
  wrap.appendChild(head);

  const intro = document.createElement("p");
  intro.className = "settings-modules-hint";
  intro.textContent =
    "Wähle ein Profil als Quick-Start, oder schreib deine eigene Persönlichkeit. Custom-Werte überschreiben das Profil. Gilt für Voice und Chat.";
  wrap.appendChild(intro);

  // Pre.152/154: Avatar-Picker. Heading + Reihe mit Kreisen + Name
  // unter jedem. Klick wählt das Profil; das Select unten ist a11y-Fallback.
  const avatarHeading = document.createElement("div");
  avatarHeading.className = "settings-label";
  avatarHeading.textContent = "Avatar";
  wrap.appendChild(avatarHeading);
  const avatarRow = document.createElement("div");
  avatarRow.className = "settings-avatar-row";
  avatarRow.setAttribute("role", "radiogroup");
  avatarRow.setAttribute("aria-label", "Bot-Avatar wählen");
  wrap.appendChild(avatarRow);

  // Profile-Picker (Select bleibt für a11y)
  const profileLabel = document.createElement("label");
  profileLabel.className = "settings-field";
  const profileLabelText = document.createElement("span");
  profileLabelText.className = "settings-label";
  profileLabelText.textContent = "Profil";
  profileLabel.appendChild(profileLabelText);
  const profileSelect = document.createElement("select");
  profileSelect.className = "settings-persona-profile";
  profileLabel.appendChild(profileSelect);
  // 2026-07-01 (Martin): gewaehlter Avatar, ENTKOPPELT vom Profil (Soul).
  let selectedAvatarKey = "";
  // 2026-07-05 (Martin): niedliche Pixel-Tier-Avatare als Auswahl. Setzen
  // customAvatarUrl auf das PNG (die Mechanik ueberschreibt den Profil-Avatar).
  const ANIMAL_AVATARS: Array<{ file: string; label: string }> = [
    { file: "baerli", label: "Bär" },
    { file: "eule", label: "Eule" },
    { file: "koala", label: "Koala" },
    { file: "wolf", label: "Wolf" },
    { file: "waldi", label: "Hund" },
    { file: "phoenix", label: "Phönix" },
  ];
  // 2026-07-05 (Martin): Tier-MARKEN im schlanken Symbol-Stil (wie Stern/Mond).
  const MARK_AVATARS: Array<{ file: string; label: string }> = [
    { file: "vespera-paw", label: "Pfote" },
    { file: "vespera-owl", label: "Eule" },
    { file: "vespera-cat", label: "Katze" },
    { file: "vespera-fox", label: "Fuchs" },
    { file: "vespera-bear", label: "Bär" },
  ];
  const animalKeyForUrl = (url?: string | null): string => {
    if (!url) return "";
    const hit = ANIMAL_AVATARS.find((an) =>
      url.includes(`/avatars/${an.file}.`),
    );
    if (hit) return `animal:${hit.file}`;
    const mark = MARK_AVATARS.find((m) =>
      url.includes(`/avatars/${m.file}.svg`),
    );
    return mark ? `mark:${mark.file}` : "";
  };
  const profileDesc = document.createElement("small");
  profileDesc.className = "settings-persona-profile-desc";
  profileLabel.appendChild(profileDesc);
  wrap.appendChild(profileLabel);

  const nameLabel = document.createElement("label");
  nameLabel.className = "settings-field";
  const nameLabelText = document.createElement("span");
  nameLabelText.className = "settings-label";
  nameLabelText.textContent = "Bot-Name (überschreibt Profil)";
  nameLabel.appendChild(nameLabelText);
  const nameInput = document.createElement("input");
  nameInput.type = "text";
  nameInput.className = "settings-persona-name";
  nameInput.maxLength = 64;
  nameInput.placeholder = "Vespera";
  nameLabel.appendChild(nameInput);
  wrap.appendChild(nameLabel);

  const soulLabel = document.createElement("label");
  soulLabel.className = "settings-field";
  const soulLabelText = document.createElement("span");
  soulLabelText.className = "settings-label";
  soulLabelText.textContent = "Persönlichkeit / Stil";
  soulLabel.appendChild(soulLabelText);

  // Pre.319: Vorlagen-Buttons — laden Profile-Soul als Inspiration in
  // den Textarea, ohne Avatar zu wechseln. Avatar (Bild+Name) ist
  // jetzt ENTKOPPELT von der Soul. Du kannst Bärli-Bild + Lina-Soul
  // mixen.
  const presetsRow = document.createElement("div");
  presetsRow.className = "settings-persona-presets";
  const presetsLabel = document.createElement("span");
  presetsLabel.className = "settings-persona-presets-label";
  presetsLabel.textContent = "Vorlage laden:";
  presetsRow.appendChild(presetsLabel);
  soulLabel.appendChild(presetsRow);

  const soulArea = document.createElement("textarea");
  soulArea.className = "settings-persona-soul";
  soulArea.rows = 14;
  soulArea.maxLength = 8000;
  soulArea.placeholder =
    "Beschreibe Wesen, Sprache, was wichtig ist (Markdown ok). Lass leer um den Profil-Soul zu nutzen.";
  soulLabel.appendChild(soulArea);
  wrap.appendChild(soulLabel);

  const autosaveHint = document.createElement("p");
  autosaveHint.className = "settings-persona-autosave-hint";
  autosaveHint.textContent = "Änderungen werden automatisch gespeichert.";
  wrap.appendChild(autosaveHint);

  const actions = document.createElement("div");
  actions.className = "settings-persona-actions";
  const save = document.createElement("button");
  save.type = "button";
  save.className = "settings-persona-save";
  save.textContent = "Jetzt speichern";
  save.disabled = true;
  actions.appendChild(save);

  const reset = document.createElement("button");
  reset.type = "button";
  reset.className = "settings-persona-reset";
  reset.textContent = "Zurücksetzen";
  actions.appendChild(reset);

  const status = document.createElement("span");
  status.className = "settings-persona-status";
  actions.appendChild(status);

  wrap.appendChild(actions);

  let initialName = "";
  let initialSoul = "";
  let initialProfileKey = "baerli";
  let defaultName = "Vespera";
  let availableProfiles: SoulProfileMeta[] = [];
  // Pre.297: Debounce-Timer für Auto-Save bei Name/Soul-Input
  let autoSaveTimer: ReturnType<typeof setTimeout> | null = null;
  let autoSaveInFlight = false;

  const triggerPersonaChanged = (): void => {
    window.dispatchEvent(new CustomEvent("voice-v8:persona-changed"));
  };

  const performAutoSave = async (): Promise<void> => {
    if (autoSaveInFlight) return;
    autoSaveInFlight = true;
    const prev = status.textContent;
    status.textContent = "speichere…";
    try {
      const cfg = await savePersona({
        botName: nameInput.value.trim(),
        soulText: soulArea.value,
        profileKey: profileSelect.value,
        avatarKey: selectedAvatarKey || undefined,
      });
      // initials aktualisieren — nicht apply() weil das die UI re-rendert
      defaultName = cfg.botName;
      initialName = cfg.isDefault ? "" : cfg.botName;
      initialSoul = cfg.soulText;
      initialProfileKey = cfg.profileKey;
      save.disabled = true;
      status.textContent = "gespeichert";
      triggerPersonaChanged();
      setTimeout(() => {
        if (status.textContent === "gespeichert") status.textContent = "";
      }, 1500);
    } catch (err) {
      status.textContent = `Fehler: ${err instanceof Error ? err.message : String(err)}`;
      // Fehler-Status länger stehen lassen
      setTimeout(() => {
        if (status.textContent?.startsWith("Fehler"))
          status.textContent = prev ?? "";
      }, 4000);
    } finally {
      autoSaveInFlight = false;
    }
  };

  const scheduleAutoSave = (delayMs: number): void => {
    if (autoSaveTimer) clearTimeout(autoSaveTimer);
    autoSaveTimer = setTimeout(() => {
      autoSaveTimer = null;
      void performAutoSave();
    }, delayMs);
  };

  const updateDirty = (): void => {
    const dirty =
      nameInput.value !== initialName ||
      soulArea.value !== initialSoul ||
      profileSelect.value !== initialProfileKey;
    save.disabled = !dirty;
  };

  const refreshProfileDesc = (): void => {
    const found = availableProfiles.find(
      (p) => p.key === profileSelect.value,
    );
    profileDesc.textContent = found ? found.description : "";
  };

  profileSelect.addEventListener("change", () => {
    const found = availableProfiles.find(
      (p) => p.key === profileSelect.value,
    );
    // Pre.319: Avatar-Wechsel setzt NUR Bild + Name-Placeholder.
    // Soul-Text bleibt unverändert — User kann ihn unabhängig
    // editieren oder eine Vorlage laden. Vorher hat der Server bei
    // Profile-Wechsel + leerem soulText automatisch den Profile-Default
    // genommen — das ist jetzt deaktiviert durch das explizite Mitschicken
    // des aktuellen Soul-Werts.
    if (found) {
      nameInput.placeholder = found.name;
      defaultName = found.name;
    }
    refreshProfileDesc();
    syncAvatarSelection();
    updateDirty();
    // Pre.297: Avatar-Wechsel speichert sofort (nicht debounced)
    if (autoSaveTimer) {
      clearTimeout(autoSaveTimer);
      autoSaveTimer = null;
    }
    void performAutoSave();
  });
  nameInput.addEventListener("input", () => {
    updateDirty();
    scheduleAutoSave(1200);
  });
  soulArea.addEventListener("input", () => {
    updateDirty();
    scheduleAutoSave(1500);
  });

  // Pre.155: Avatar-Bild aus avatarUrl rendern (echte v7-Avatare statt
  // Initialen-Kreise). Fallback-Farben pro Key falls Bild fehlt.
  const renderProfileOptions = (profiles: SoulProfileMeta[]): void => {
    while (profileSelect.firstChild) {
      profileSelect.removeChild(profileSelect.firstChild);
    }
    for (const p of profiles) {
      const opt = document.createElement("option");
      opt.value = p.key;
      opt.textContent = p.label;
      profileSelect.appendChild(opt);
    }

    // Pre.319: Vorlagen-Buttons rendern (existing presetsRow leeren, dann
    // einen Button pro Profile). Click → fetcht Profile-Soul + füllt
    // Textarea. Berührt Avatar NICHT.
    while (presetsRow.children.length > 1) {
      presetsRow.removeChild(presetsRow.lastChild!);
    }
    for (const p of profiles) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "settings-persona-preset-btn";
      btn.textContent = p.name;
      btn.title = `${p.name}-Vorlage in Textarea laden — Avatar bleibt unverändert`;
      btn.addEventListener("click", async () => {
        try {
          const res = await fetch(
            `/api/persona/profile/${encodeURIComponent(p.key)}`,
            { credentials: "same-origin" },
          );
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const data = (await res.json()) as { soulText?: string };
          if (typeof data.soulText === "string") {
            soulArea.value = data.soulText;
            soulArea.dispatchEvent(new Event("input"));
            opts.flashStatus(`Vorlage "${p.name}" geladen`);
          }
        } catch (err) {
          opts.flashStatus(
            "Konnte Vorlage nicht laden: " +
              (err instanceof Error ? err.message : String(err)),
          );
        }
      });
      presetsRow.appendChild(btn);
    }

    // Avatar-Reihe synchronisieren
    while (avatarRow.firstChild) avatarRow.removeChild(avatarRow.firstChild);
    for (const p of profiles) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "settings-avatar";
      btn.dataset.key = p.key;
      btn.setAttribute("role", "radio");
      btn.setAttribute("aria-checked", "false");
      btn.title = `${p.name} — ${p.description}`;

      const circle = document.createElement("span");
      circle.className = "settings-avatar-circle";
      // Pre.162: Thumb für Picker — Original nur ~30KB statt 1.4MB.
      const src = p.avatarThumbUrl ?? p.avatarUrl;
      if (src) {
        haengeAvatarBild(circle, src, p.name, p.key);
      } else {
        circle.dataset.fallback = p.key;
        circle.textContent = p.name.charAt(0).toUpperCase() || "?";
      }
      btn.appendChild(circle);

      const name = document.createElement("span");
      name.className = "settings-avatar-name";
      name.textContent = p.name;
      btn.appendChild(name);

      btn.addEventListener("click", () => {
        // 2026-07-01 (Martin): setzt den AVATAR (Bild), nicht die
        // Persoenlichkeit — die waehlt weiter das Profil-Dropdown.
        selectedAvatarKey = p.key;
        syncAvatarSelection();
        void savePersona({
          botName: nameInput.value.trim(),
          soulText: soulArea.value,
          profileKey: profileSelect.value,
          avatarKey: p.key,
        })
          .then(() => opts.flashStatus("Avatar gesetzt"))
          .catch(() => {});
      });
      avatarRow.appendChild(btn);
    }

    // 2026-07-05 (Martin): Tier-Marken (Symbol-Stil, SVG) als Auswahl.
    for (const mk of MARK_AVATARS) {
      const key = `mark:${mk.file}`;
      const url = `/avatars/${mk.file}.svg`;
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "settings-avatar";
      btn.dataset.key = key;
      btn.setAttribute("role", "radio");
      btn.setAttribute("aria-checked", "false");
      btn.title = `${mk.label} (Marke) als Bot-Avatar`;
      const circle = document.createElement("span");
      circle.className = "settings-avatar-circle";
      haengeAvatarBild(circle, url, mk.label, mk.file);
      btn.appendChild(circle);
      const name = document.createElement("span");
      name.className = "settings-avatar-name";
      name.textContent = mk.label;
      btn.appendChild(name);
      btn.addEventListener("click", () => {
        selectedAvatarKey = key;
        syncAvatarSelection();
        void savePersona({
          botName: nameInput.value.trim(),
          soulText: soulArea.value,
          profileKey: profileSelect.value,
          avatarKey: "custom",
          customAvatarUrl: url,
        })
          .then(() => {
            triggerPersonaChanged();
            opts.flashStatus("Avatar gesetzt");
          })
          .catch(() => {});
      });
      avatarRow.appendChild(btn);
    }

    // 2026-07-05 (Martin): Tier-Avatare (Pixel-Art) als weitere Auswahl.
    for (const an of ANIMAL_AVATARS) {
      const key = `animal:${an.file}`;
      const url = `/avatars/${an.file}.png`;
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "settings-avatar";
      btn.dataset.key = key;
      btn.setAttribute("role", "radio");
      btn.setAttribute("aria-checked", "false");
      btn.title = `${an.label} als Bot-Avatar`;
      const circle = document.createElement("span");
      circle.className = "settings-avatar-circle";
      haengeAvatarBild(
        circle,
        `/avatars/${an.file}.thumb.webp`,
        an.label,
        an.file,
      );
      btn.appendChild(circle);
      const name = document.createElement("span");
      name.className = "settings-avatar-name";
      name.textContent = an.label;
      btn.appendChild(name);
      btn.addEventListener("click", () => {
        selectedAvatarKey = key;
        syncAvatarSelection();
        void savePersona({
          botName: nameInput.value.trim(),
          soulText: soulArea.value,
          profileKey: profileSelect.value,
          avatarKey: "custom",
          customAvatarUrl: url,
        })
          .then(() => {
            triggerPersonaChanged();
            opts.flashStatus("Avatar gesetzt");
          })
          .catch(() => {});
      });
      avatarRow.appendChild(btn);
    }

    // Pre.155: Custom-Avatar-Upload als 8. Tile am Ende
    const upload = document.createElement("button");
    upload.type = "button";
    upload.className = "settings-avatar settings-avatar-upload";
    upload.title = "Eigenes Avatar hochladen";
    upload.setAttribute(
      "aria-label",
      "Eigenes Bild als Bot-Avatar hochladen",
    );
    const uploadCircle = document.createElement("span");
    uploadCircle.className = "settings-avatar-circle";
    uploadCircle.textContent = "+";
    upload.appendChild(uploadCircle);
    const uploadLabel = document.createElement("span");
    uploadLabel.className = "settings-avatar-name";
    uploadLabel.textContent = "Eigenes";
    upload.appendChild(uploadLabel);
    upload.dataset.key = "custom";
    upload.addEventListener("click", () => void handleAvatarUpload(opts.flashStatus, opts.onAvatarSaved));
    avatarRow.appendChild(upload);
  };

  const syncAvatarSelection = (): void => {
    for (const btn of avatarRow.querySelectorAll<HTMLButtonElement>(
      ".settings-avatar",
    )) {
      const isActive = btn.dataset.key === selectedAvatarKey;
      btn.dataset.active = String(isActive);
      btn.setAttribute("aria-checked", String(isActive));
    }
  };

  const apply = (cfg: {
    botName: string;
    soulText: string;
    profileKey: string;
    isDefault: boolean;
    availableProfiles: SoulProfileMeta[];
    avatarKey?: string | null;
    customAvatarUrl?: string | null;
    innerGuidance?: string | null;
    innerGuidanceAktiv?: boolean;
  }): void => {
    availableProfiles = cfg.availableProfiles;
    renderProfileOptions(availableProfiles);
    defaultName = cfg.botName;
    initialName = cfg.isDefault ? "" : cfg.botName;
    initialSoul = cfg.soulText;
    initialProfileKey = cfg.profileKey;
    nameInput.value = initialName;
    nameInput.placeholder = defaultName;
    soulArea.value = initialSoul;
    profileSelect.value = initialProfileKey;
    // 2026-07-01 (Martin): Avatar folgt avatarKey, sonst (Altdaten) dem Profil.
    selectedAvatarKey =
      animalKeyForUrl(cfg.customAvatarUrl) || cfg.avatarKey || cfg.profileKey;
    refreshProfileDesc();
    syncAvatarSelection();
    save.disabled = true;

    // Die innere Ausrichtung — nur zeigen, wenn es sie gibt.
    const g = (cfg.innerGuidance ?? "").trim();
    ausrichtung.hidden = !g;
    if (g) {
      ausrichtungText.textContent = g;
      const an = cfg.innerGuidanceAktiv !== false;
      ausrichtungBox.checked = an;
      ausrichtungWort.textContent = an
        ? "Ausrichtung aktiv"
        : "Ausrichtung aus";
      ausrichtungHerkunft.textContent =
        "Dieser Text steht seit 15.07.2026 in Vesperas Wesenskern und färbt " +
        "jede Antwort zu persönlichen Themen. Er stammt nicht aus deiner " +
        "Feder — laut deiner eigenen Notiz vom selben Tag kam er von außen. " +
        "Bei sachlichen Dingen tritt er zurück.";
    }
  };

  save.addEventListener("click", async () => {
    save.disabled = true;
    status.textContent = "speichere…";
    try {
      const cfg = await savePersona({
        botName: nameInput.value.trim(),
        soulText: soulArea.value,
        profileKey: profileSelect.value,
        avatarKey: selectedAvatarKey || undefined,
      });
      apply(cfg);
      status.textContent = "gespeichert";
      setTimeout(() => (status.textContent = ""), 1500);
    } catch (err) {
      status.textContent = `Fehler: ${err instanceof Error ? err.message : String(err)}`;
      save.disabled = false;
    }
  });

  reset.addEventListener("click", async () => {
    if (!confirm("Persönlichkeit auf Standard zurücksetzen?")) return;
    try {
      const cfg = await resetPersona();
      apply(cfg);
      status.textContent = "zurückgesetzt";
      setTimeout(() => (status.textContent = ""), 1500);
    } catch (err) {
      status.textContent = `Fehler: ${err instanceof Error ? err.message : String(err)}`;
    }
  });

  /*
   * ══ DIE INNERE AUSRICHTUNG — sichtbar gemacht am 29.08.2026 ══════════════
   *
   * Martin: „Ich moechte erkennen, wie selbstbestimmt ich die Dinge ueberhaupt
   * noch wahrnehme oder wie stark eingefaerbt sie durch ihre Praegungen sind."
   *
   * Die Analyse ergab: Dieser Text ist der EINZIGE lebensnormative Block im
   * ganzen System-Prompt — 18 Anweisungen, an Position 3 von 54, also VOR
   * allen Regeln — und er trug KEINEN Herkunftsvermerk. In der Oberflaeche
   * war er nicht zu sehen. Seit dem 15.07.2026.
   *
   * ⚠️ Er wird hier NICHT zum Bearbeiten angeboten und nicht geloescht. Nur
   * gezeigt und abschaltbar. Ein Text, den man abschalten kann, ist ein Rat;
   * einer, den man loeschen muss, ist ein Streit.
   *
   * ⚠️ Seit 23.09.2026 (Ticket 2550) ist er nicht mehr der einzige: Der feste
   *    Baustein INNEN UND AUSSEN (agent/persona.ts) wiederholt seine
   *    Kernregel „Du-Bezug statt Selbstberuhigung", gilt für alle Konten und
   *    hängt NICHT an diesem Schalter. Martin am 24.09.2026: ein eigener
   *    Schalter — „Innen und außen", gleich unter diesem Block
   *    (settings-view.ts, selbstermaechtigungToggle; Einstellungsfeld statt
   *    Persona, damit er für jedes Konto gilt).
   */
  const ausrichtung = document.createElement("details");
  ausrichtung.className = "settings-ausrichtung";
  ausrichtung.hidden = true;

  const ausrichtungTitel = document.createElement("summary");
  ausrichtungTitel.className = "settings-ausrichtung-titel";
  ausrichtungTitel.textContent = "Innere Ausrichtung";
  ausrichtung.appendChild(ausrichtungTitel);

  const ausrichtungHerkunft = document.createElement("p");
  ausrichtungHerkunft.className = "settings-ausrichtung-herkunft";
  ausrichtung.appendChild(ausrichtungHerkunft);

  const ausrichtungSchalter = document.createElement("label");
  ausrichtungSchalter.className = "settings-ausrichtung-schalter";
  const ausrichtungBox = document.createElement("input");
  ausrichtungBox.type = "checkbox";
  ausrichtungBox.checked = true;
  const ausrichtungWort = document.createElement("span");
  ausrichtungWort.textContent = "Ausrichtung aktiv";
  ausrichtungSchalter.append(ausrichtungBox, ausrichtungWort);
  ausrichtung.appendChild(ausrichtungSchalter);

  const ausrichtungText = document.createElement("pre");
  ausrichtungText.className = "settings-ausrichtung-text";
  ausrichtung.appendChild(ausrichtungText);

  ausrichtungBox.addEventListener("change", () => {
    const an = ausrichtungBox.checked;
    ausrichtungWort.textContent = an ? "Ausrichtung aktiv" : "Ausrichtung aus";
    status.textContent = "speichere…";
    void savePersona({
      botName: nameInput.value.trim(),
      soulText: soulArea.value,
      profileKey: profileSelect.value,
      avatarKey: selectedAvatarKey || undefined,
      innerGuidanceAktiv: an,
    })
      .then(() => {
        status.textContent = an
          ? "Ausrichtung wieder aktiv"
          : "Ausrichtung ausgeschaltet";
        triggerPersonaChanged();
      })
      .catch((err) => {
        ausrichtungBox.checked = !an;
        status.textContent = `Fehler: ${err instanceof Error ? err.message : String(err)}`;
      });
  });

  wrap.appendChild(ausrichtung);

  void fetchPersona()
    .then(apply)
    .catch((err) => {
      status.textContent = `Konnte Persönlichkeit nicht laden: ${err instanceof Error ? err.message : String(err)}`;
    });

  return wrap;
}
